import { format } from 'date-fns';

import {
  DatetimeFormatType,
  DatetimeMonthFormat,
  FormatDateValueType,
  FormatType,
  FriendlyAdditions,
  i18nextFormatValueType,
  TypeOfDate,
} from 'app/lib/datetime/types';

import { type I18Lang, tFunction } from 'app/lang';
import { SupportedLang } from 'app/lang/constants';
import { isSupportedLang } from 'app/lang/utils/supported-lang-functions';
import { getFriendlyFormatValue } from 'app/lib/datetime/friendlyDatetime';
import { formatByLang, getDefaultFormat, isCurrentYear } from 'app/lib/datetime/utils';

// ---

// TODO: Закомментировал все, что связано с relative форматом.
// Так как в dateFNS нет нормального relative формата,
// мы будем описывать его ручками. Прямо как с friendly форматом.

// ---

export function formatValueToDate(value: FormatDateValueType): Date {
  // На самом деле, если в value придёт просто строка, например, пустая,
  // то на выходе мы получим Invalid Date и это всё сломает.
  return value instanceof Date ? value : new Date(value);
}

export function formatDate(
  value: FormatDateValueType,
  datetimeFormat: DatetimeFormatType,
  lang: I18Lang,
  options?: {
    monthFormat?: DatetimeMonthFormat;
    friendlyFormat?: FriendlyAdditions;
    typeOfDate?: TypeOfDate;
    withoutYear?: boolean;
  },
  t?: tFunction,
): string {
  const date = formatValueToDate(value);

  if (!isSupportedLang(lang)) {
    console.error(`(formatDate) Unknown lang = '${lang}'`);

    return getDefaultFormat(date, isCurrentYear(date), options?.monthFormat);
  }

  switch (datetimeFormat) {
    case DatetimeFormatType.NUMERIC:
    case DatetimeFormatType.NUMERIC_WITH_TIME:
    case DatetimeFormatType.TIME:
    case DatetimeFormatType.TIME_WITH_SECONDS:
      return formatByLang(date, lang, datetimeFormat);

    case DatetimeFormatType.SHORT: {
      return formatByLang(
        date,
        lang,
        isCurrentYear(date) ? 'short' : 'shortWithYear',
        options?.monthFormat,
      );
    }

    case DatetimeFormatType.LONG: {
      return formatByLang(
        date,
        lang,
        isCurrentYear(date) ? 'long' : 'longWithYear',
        options?.monthFormat,
      );
    }

    case DatetimeFormatType.FRIENDLY: {
      return getFriendlyFormatValue(
        date,
        lang,
        {
          monthFormat: options?.monthFormat,
          typeOfDate: options?.typeOfDate,
          friendlyFormat: options?.friendlyFormat,
          withoutYear: options?.withoutYear,
        },
        t,
      );
    }

    // case 'relative': {
    //   const diff = differenceInMilliseconds(new Date(), date);
    //
    //   if (diff > 0 && diff < 10000) {
    //     return DATE_FORMATS()[lang].just;
    //   }
    // }

    default: {
      console.error(`Unknown format '${format}'`);
      return getDefaultFormat(date, isCurrentYear(date), options?.monthFormat);
    }
  }
}

export function i18nextFormat(
  value: i18nextFormatValueType,
  format?: FormatType | string,
  lang?: SupportedLang | string,
): string {
  if (!isSupportedLang(lang)) {
    console.error(`(i18nextFormat) Unknown lang = '${lang}'`);
    return '';
  }

  if (format === 'datetime') {
    if (!value?.date || !value?.format) {
      console.error('The value should be of the form: { date: new Date(), format: DatetimeFormatType }');
      return '';
    }

    return formatDate(
      value.date,
      value.format,
      lang,
      {
        monthFormat: value.monthFormat,
        typeOfDate: value.typeOfDate,
        friendlyFormat: value.friendlyFormat,
      },
    );
  }

  return '';
}
// ---

export { isToday } from './friendlyDatetime';
